import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTrophy, 
  faCircleCheck, 
  faCircleXmark, 
  faVolumeHigh,
  faHouse,
  faBook,
  faChartSimple,
  faGear,
  faCheck,
  faTimes,
  faCheckDouble,
  faXmark,
  faClock,
  faFire,
  faCamera
} from '@fortawesome/free-solid-svg-icons';
import WordPlayer from './WordPlayer'; // 导入单词播放组件
import confetti from 'canvas-confetti'; // 直接导入 
import { createConfetti, createPerfectCelebration } from '../utils/animations'; // 导入动画工具
import CameraCheckModal from './CameraCheckModal'; // 导入拍照检查组件


const CheckResultPage = ({ 
  results = [], 
  bookInfo = { grade: '三年级', term: '上册', unit: 'Unit 2' },
  correctCount = 17,
  totalCount = 20,
  dictationTime = new Date().toISOString() // 添加听写时间参数
}) => {
  const navigate = useNavigate();
  // 添加动画状态引用
  const animationTriggeredRef = useRef(false);

  // 添加状态来跟踪手动校验的结果
  const [checkedResults, setCheckedResults] = useState([]);
  const [manualCorrectCount, setManualCorrectCount] = useState(0);
  const [manualWrongCount, setManualWrongCount] = useState(0);
  const [manualCorrectRate, setManualCorrectRate] = useState(0);
  // 添加检查状态
  const [checkingStatus, setCheckingStatus] = useState('initial'); // initial, checking, completed
  // 添加是否全部检查完成的状态
  const [allChecked, setAllChecked] = useState(false);
  // 格式化听写时间
  const [formattedTime, setFormattedTime] = useState('');
  
  // 添加拍照检查相关状态
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [processingImage, setProcessingImage] = useState(false);
  
  // 计算圆形进度条的参数
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (manualCorrectRate / 100) * circumference;
  
  // 示例结果数据
  const sampleResults = [
    { id: 1, word: 'apple', isCorrect: null },
    { id: 2, word: 'banana', isCorrect: null },
    { id: 3, word: 'grape', isCorrect: null },
    { id: 4, word: 'peach', userAnswer: 'peech', isCorrect: null },
    { id: 5, word: 'pear', isCorrect: null },
    { id: 6, word: 'watermelon', isCorrect: null },
  ];
  
  const displayResults = results.length > 0 ? results : sampleResults;
  
  // 初始化手动校验结果 - 默认所有单词都是未检查状态
  useEffect(() => {
    setCheckedResults(displayResults.map(item => ({ 
      ...item, 
      isCorrect: null // 初始状态为null，表示未检查
    })));
  }, [displayResults]);

  // 格式化时间
  useEffect(() => {
    const formatDate = (dateString) => {
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return '今天';
        }
        
        const now = new Date();
        const isToday = date.getDate() === now.getDate() && 
                        date.getMonth() === now.getMonth() && 
                        date.getFullYear() === now.getFullYear();
        
        if (isToday) {
          return `今天 ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
        } else {
          return `${date.getMonth() + 1}月${date.getDate()}日 ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
        }
      } catch (error) {
        console.error('日期格式化错误:', error);
        return '今天';
      }
    };
    
    setFormattedTime(formatDate(dictationTime));
  }, [dictationTime]);
  
  // 监听状态变化，触发动画
  useEffect(() => {
    // 如果状态变为completed且动画未触发过，则触发动画
    if (checkingStatus === 'completed' && !animationTriggeredRef.current) {
      // 标记动画已触发
      animationTriggeredRef.current = true;
      
      // 根据正确率决定播放哪种动画
      if (manualCorrectRate === 100) {
        // 全对，播放完美庆祝动画
        setTimeout(() => {
          createPerfectCelebration();
        }, 300);
      } else {
        // 普通完成，播放普通撒花
        setTimeout(() => {
          createConfetti();
        }, 300);
      }
    }
  }, [checkingStatus, manualCorrectRate]);
  
  // 处理手动标记正确/错误
  const handleToggleCorrect = (index) => {
    // 如果已经完成检查，不允许再操作
    if (checkingStatus === 'completed') {
      return;
    }
    
    // 如果状态是initial，切换到checking
    if (checkingStatus === 'initial') {
      setCheckingStatus('checking');
    }
    
    const newResults = [...checkedResults];
    // 如果当前是null，设为true；如果是true，设为false；如果是false，设为true
    if (newResults[index].isCorrect === null) {
      newResults[index].isCorrect = true;
    } else if (newResults[index].isCorrect === true) {
      newResults[index].isCorrect = false;
    } else {
      newResults[index].isCorrect = true;
    }
    
    setCheckedResults(newResults);
    
    // 更新正确数量和错误数量
    updateCounts(newResults);
    
    // 检查是否所有单词都已标记
    checkAllMarked(newResults);
  };
  
  // 检查是否所有单词都已标记
  const checkAllMarked = (results) => {
    const allMarked = results.every(item => item.isCorrect !== null);
    setAllChecked(allMarked);
  };
  
  // 更新计数和正确率的函数
  const updateCounts = (results) => {
    const newCorrectCount = results.filter(item => item.isCorrect === true).length;
    const newWrongCount = results.filter(item => item.isCorrect === false).length;
    const markedCount = newCorrectCount + newWrongCount;
    const newCorrectRate = markedCount > 0 ? Math.round((newCorrectCount / markedCount) * 100) : 0;
    
    setManualCorrectCount(newCorrectCount);
    setManualWrongCount(newWrongCount);
    setManualCorrectRate(newCorrectRate);
  };
  
  // 一键全对功能
  const handleMarkAllCorrect = () => {
    // 如果已经完成检查，不允许再操作
    if (checkingStatus === 'completed') {
      return;
    }
    
    // 如果状态是initial，切换到checking
    if (checkingStatus === 'initial') {
      setCheckingStatus('checking');
    }
    
    const newResults = checkedResults.map(item => ({
      ...item,
      isCorrect: true
    }));
    setCheckedResults(newResults);
    updateCounts(newResults);
    setAllChecked(true);
  };
  
  // 一键全错功能
  const handleMarkAllWrong = () => {
    // 如果已经完成检查，不允许再操作
    if (checkingStatus === 'completed') {
      return;
    }
    
    // 如果状态是initial，切换到checking
    if (checkingStatus === 'initial') {
      setCheckingStatus('checking');
    }
    
    const newResults = checkedResults.map(item => ({
      ...item,
      isCorrect: false
    }));
    setCheckedResults(newResults);
    updateCounts(newResults);
    setAllChecked(true);
  };
  
  // 完成检查
  const handleCompleteChecking = () => {
    // 检查是否所有单词都已标记
    if (!allChecked) {
      const unCheckedCount = checkedResults.filter(item => item.isCorrect === null).length;
      alert(`还有 ${unCheckedCount} 个单词未检查，请完成所有单词的检查！`);
      return;
    }
    
    // 记录听写结果
    saveResults();
    
    // 更新状态为已完成
    setCheckingStatus('completed');
    
    // 动画会在useEffect中触发
  };
  
  // 保存结果到本地存储或发送到服务器
  const saveResults = () => {
    const result = {
      date: dictationTime || new Date().toISOString(),
      bookInfo,
      totalCount: checkedResults.length,
      correctCount: manualCorrectCount,
      wrongCount: manualWrongCount,
      correctRate: manualCorrectRate,
      words: checkedResults
    };
    
    // 保存到本地存储
    try {
      const historyResults = JSON.parse(localStorage.getItem('dictationResults') || '[]');
      historyResults.push(result);
      localStorage.setItem('dictationResults', JSON.stringify(historyResults));
      console.log('听写结果已保存');
    } catch (error) {
      console.error('保存听写结果失败:', error);
    }
    
    // 也可以发送到服务器
    // TODO: 实现发送到服务器的逻辑
  };
  
  const handleRetry = () => {
    navigate('/dictation');
  };
  
  const handleBackToHome = () => {
    navigate('/');
  };
  
  // 处理拍照检查 - 移动到组件内部
  const handleCameraCheck = () => {
    setShowCameraModal(true);
  };

  // 处理拍照结果 - 移动到组件内部
  const handlePhotoResult = async (imageData) => {
    setProcessingImage(true);
    
    try {
      // 准备要发送给AI的单词列表
      const wordsList = checkedResults.map(item => item.word).join(', ');
      
      // 调用AI处理图片和单词
      const aiResults = await processImageWithAI(imageData, wordsList);
      
      // 更新检查结果
      if (aiResults && aiResults.length > 0) {
        const newResults = [...checkedResults];
        
        aiResults.forEach(result => {
          const index = newResults.findIndex(item => 
            item.word.toLowerCase() === result.word.toLowerCase());
          
          if (index !== -1) {
            newResults[index].isCorrect = result.isCorrect;
          }
        });
        
        setCheckedResults(newResults);
        updateCounts(newResults);
        checkAllMarked(newResults);
      }
      
      // 关闭相机模态框
      setShowCameraModal(false);
    } catch (error) {
      console.error('AI处理图片失败:', error);
      alert('AI处理图片失败，请重试或手动检查');
    } finally {
      setProcessingImage(false);
    }
  };

  // 调用OpenRouter AI处理图片 - 使用CORS代理
  const processImageWithAI = async (imageData, wordsList) => {
    try {
      // 确保图片数据是正确的base64格式
      let base64Image = imageData;
      if (imageData.startsWith('data:')) {
        base64Image = imageData.split(',')[1];
      }
      
      // 使用CORS代理
      const corsProxy = 'https://cors-anywhere.herokuapp.com/';
      const apiUrl = 'https://openrouter.ai/api/v1/chat/completions';
      
      const response = await fetch(corsProxy + apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer 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`,
          'Origin': window.location.origin,
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          model: 'qwen/qwen2.5-vl-32b-instruct:free',
          messages: [
            {
              role: 'system',
              content: '你是一个专业的英语听写检查助手，你的任务是检查学生的英语单词听写结果是否正确。'
            },
            {
              role: 'user',
              content: [
                {
                  type: 'text',
                  text: `请检查图片中的英语单词听写结果。标准单词列表是: ${wordsList}。
                  请按照以下JSON格式返回检查结果:
                  [
                    {"word": "单词1", "isCorrect": true/false},
                    {"word": "单词2", "isCorrect": true/false},
                    ...
                  ]
                  只返回JSON数据，不要有其他文字。`
                },
                {
                  type: 'image_url',
                  image_url: {
                    url: `data:image/jpeg;base64,${base64Image}`
                  }
                }
              ]
            }
          ]
        })
      });
      
      const data = await response.json();
      
      if (data.choices && data.choices.length > 0) {
        const content = data.choices[0].message.content;
        // 尝试解析JSON响应
        try {
          // 提取JSON部分（防止AI返回额外文本）
          const jsonMatch = content.match(/\[[\s\S]*\]/);
          if (jsonMatch) {
            return JSON.parse(jsonMatch[0]);
          }
          return JSON.parse(content);
        } catch (e) {
          console.error('解析AI响应失败:', e);
          console.log('原始响应:', content);
          throw new Error('AI返回的数据格式不正确');
        }
      }
      
      throw new Error('AI未返回有效结果');
    } catch (error) {
      console.error('调用AI服务失败:', error);
      throw error;
    }
  };
  
  return (
    <div className="bg-[#f9f9f9] min-h-screen">
      {/* Main Content */}
      <div className="p-5">
        {/* Navigation Header */}
        <div className="flex justify-between items-center mb-6">
          <div></div>
          <h2 className="text-xl font-bold">听写结果</h2>
          <div></div>
        </div>

        {/* Results Summary */}
        <div className="bg-white rounded-xl p-5 shadow-sm text-center mb-6">
          <div className="mb-4">
            <div className="inline-block rounded-full bg-green-100 p-3 mb-2">
              <FontAwesomeIcon 
                icon={manualCorrectRate === 100 && checkingStatus === 'completed' ? faFire : faTrophy} 
                className={`text-2xl ${manualCorrectRate === 100 && checkingStatus === 'completed' ? 'text-pink-500' : 'text-yellow-500'}`} 
              />
            </div>
            <h3 className="text-xl font-bold">
              {checkingStatus === 'completed' 
                ? manualCorrectRate === 100 
                  ? '太棒了！全部正确！' 
                  : '检查完成！' 
                : '开始检查'}
            </h3>
            <p className="text-gray-500 text-sm mb-2">
              {bookInfo.grade}{bookInfo.term} {bookInfo.unit} · {totalCount}个单词
            </p>
            {/* 显示听写时间 */}
            <p className="text-gray-500 text-sm mb-3 flex items-center justify-center">
              <FontAwesomeIcon icon={faClock} className="mr-1" />
              <span>{formattedTime}</span>
            </p>
            {checkingStatus === 'initial' && (
              <p className="text-blue-500 text-sm">
                请对照纸质作业本，标记每个单词的正确与否
              </p>
            )}
          </div>
          
          {/* Score Circle - 只在有标记的单词时显示 */}
          {(manualCorrectCount > 0 || manualWrongCount > 0) && (
            <>
              <div className="inline-flex items-center justify-center mb-3">
                <div className="relative">
                  <svg className="w-28 h-28">
                    <circle 
                      cx="56" 
                      cy="56" 
                      r="50"
                      fill="none"
                      stroke="#f0f0f0"
                      strokeWidth="10"
                    />
                    <circle 
                      cx="56" 
                      cy="56" 
                      r="50"
                      fill="none"
                      stroke={manualCorrectRate === 100 ? "#ff6b6b" : "#34d399"}
                      strokeWidth="10"
                      strokeDasharray={circumference}
                      strokeDashoffset={offset}
                      transform="rotate(-90 56 56)"
                      className={manualCorrectRate === 100 && checkingStatus === 'completed' ? "animate-pulse" : ""}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center flex-col">
                    <span className={`text-3xl font-bold ${manualCorrectRate === 100 && checkingStatus === 'completed' ? "text-pink-600" : ""}`}>
                      {manualCorrectRate}%
                    </span>
                    <span className="text-xs text-gray-500">正确率</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-center space-x-6 text-center">
                <div>
                  <div className="text-green-500 font-bold text-xl">{manualCorrectCount}</div>
                  <div className="text-xs text-gray-500">正确</div>
                </div>
                <div>
                  <div className="text-red-500 font-bold text-xl">{manualWrongCount}</div>
                  <div className="text-xs text-gray-500">错误</div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Word List */}
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-bold text-lg">单词列表</h3>
          <div className="flex space-x-2">
            {/* 添加拍照检查按钮 */}
            <button 
              onClick={handleCameraCheck}
              className={`flex items-center bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm ${
                checkingStatus === 'completed' ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              disabled={checkingStatus === 'completed' || processingImage}
            >
              <FontAwesomeIcon icon={faCamera} className="mr-1" />
              拍照检查
            </button>
            <button 
              onClick={handleMarkAllCorrect}
              className={`flex items-center bg-green-100 text-green-600 px-3 py-1 rounded-full text-sm ${
                checkingStatus === 'completed' ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              disabled={checkingStatus === 'completed'}
            >
              <FontAwesomeIcon icon={faCheckDouble} className="mr-1" />
              全对
            </button>
            <button 
              onClick={handleMarkAllWrong}
              className={`flex items-center bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm ${
                checkingStatus === 'completed' ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              disabled={checkingStatus === 'completed'}
            >
              <FontAwesomeIcon icon={faXmark} className="mr-1" />
              全错
            </button>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm mb-8">
          {checkedResults.map((item, index) => (
            <div 
              key={item.id} 
              className={`p-3 flex items-center ${
                index < checkedResults.length - 1 ? 'border-b border-gray-100' : ''
              }`}
            >
              <div className="w-8 h-8 flex items-center justify-center mr-3">
                <span className="text-sm font-medium">{index + 1}</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center">
                  <span className="font-medium mr-2">{item.word}</span>
                 
                </div>
                {!item.isCorrect && item.userAnswer && (
                  <div className="text-sm text-red-500 mt-1 hidden">
                    你的答案: {item.userAnswer}
                  </div>
                )}
              </div>
              {/* 使用WordPlayer组件替换原来的播放按钮 */}
              <WordPlayer 
                word={item.word} 
                className="mr-4"
                options={{ rate: 0.8 }}
              />
              <button 
                onClick={() => handleToggleCorrect(index)}
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  item.isCorrect === true 
                    ? 'bg-green-500 text-white' 
                    : item.isCorrect === false 
                      ? 'bg-red-500 text-white' 
                      : 'bg-gray-100 text-gray-400'
                } ${checkingStatus === 'completed' ? 'opacity-80 cursor-not-allowed' : ''}`}
                disabled={checkingStatus === 'completed'}
              >
                {item.isCorrect === true ? (
                  <FontAwesomeIcon icon={faCheck} />
                ) : item.isCorrect === false ? (
                  <FontAwesomeIcon icon={faTimes} />
                ) : (
                  <span className="text-xs">?</span>
                )}
              </button>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 gap-4">
          {checkingStatus === 'initial' || checkingStatus === 'checking' ? (
            <button 
              className="bg-blue-500 text-white py-3 rounded-xl font-bold shadow-sm"
              onClick={handleCompleteChecking}
            >
              完成检查
            </button>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <button 
                className="bg-white text-blue-600 border border-blue-200 py-3 rounded-xl font-bold shadow-sm"
                onClick={handleBackToHome}
              >
                返回首页
              </button>
              <button 
                className="bg-blue-500 text-white py-3 rounded-xl font-bold shadow-sm"
                onClick={handleRetry}
              >
                再次听写
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* 拍照检查模态框 */}
      {showCameraModal && (
        <CameraCheckModal 
          onClose={() => setShowCameraModal(false)}
          onPhotoTaken={handlePhotoResult}
          processingImage={processingImage}
        />
      )}
    </div>
  );
};

export default CheckResultPage;